// Браузерный голосовой ввод: чистая обёртка над Web Speech API.
//
// Вынесена из AntDesignXChatSurface, чтобы транскрипт/старт/стоп можно было
// тестировать без AntD X Sender (в jsdom его prefix-слот не рендерится) и
// без реального микрофона: recognition-объект приходит инъекцией.
//
// Приватность соблюдается и здесь, и вызывающей стороной: речь не пишется ни
// в файл, ни на сервер — только `transcript` в локальный draft сообщения.
//
// Модель событий (Web Speech API, `interimResults=true`, `continuous=false`):
// каждый `onresult` содержит полную растущую гипотезу в `results[0][0]`
// (`resultIndex=0`), а не дельта-фрагмент. `isFinal`-результаты закрепляют
// реплику; живой хвост заменяет предыдущий interim. Поэтому сбор не
// накапливает: finals складываются, tail заменяется, итог — одна строка.

export interface TranscriptResult {
  transcript: string;
  isFinal?: boolean;
}

interface RecognitionResultEvent {
  resultIndex: number;
  results: Array<ReadonlyArray<TranscriptResult>>;
}

export interface SpeechRecognitionLike {
  lang: string | null;
  continuous: boolean;
  interimResults: boolean;
  onstart: (() => void) | null;
  onresult: ((event: RecognitionResultEvent) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

export type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

export interface SpeechRecognitionCallbacks {
  onStart: () => void;
  /** Полная текущая гипотеза распознавания (finals + живой хвост). */
  onResult: (transcript: string) => void;
  onEnd: () => void;
  onError: () => void;
}

/** Накопленное состояние распознавания: закреплённые реплики + живой хвост. */
export interface TranscriptHypothesis {
  finals: readonly string[];
  tail: string;
}

export const EMPTY_HYPOTHESIS: TranscriptHypothesis = { finals: [], tail: '' };

/**
 * Применяет `onresult`-событие к состоянию: `isFinal`-реплики закрепляются,
 * interim-хвост заменяется. Возвращает новое состояние (чистая функция).
 */
export function nextHypothesis(
  event: RecognitionResultEvent,
  state: TranscriptHypothesis,
): TranscriptHypothesis {
  const finals = state.finals.slice();
  let tail = '';
  for (let i = event.resultIndex; i < event.results.length; i += 1) {
    const first = event.results[i]?.[0];
    if (!first) continue;
    if (first.isFinal === true) {
      finals.push(first.transcript);
    } else {
      tail = first.transcript;
    }
  }
  return { finals, tail };
}

/** Текст гипотезы: закреплённые реплики через пробел + живой хвост. */
export function hypothesisText(state: TranscriptHypothesis): string {
  const base = state.finals.join(' ');
  if (state.tail && base) return `${base} ${state.tail}`;
  return state.tail || base;
}

/**
 * Находит конструктор распознавания в окне (стандарт или `webkit`-префикс).
 * `null` — браузер не поддерживает голосовой ввод.
 */
export function resolveSpeechRecognition(
  speechWindow: Window & {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  },
): SpeechRecognitionConstructor | null {
  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition ?? null;
}

export interface StartRecognitionOptions {
  locale: string;
  callbacks: SpeechRecognitionCallbacks;
  constructor: SpeechRecognitionConstructor;
}

/**
 * Поднимает сессию распознавания. Возвращает `SpeechRecognitionLike` (для
 * остановки вызывающей стороной). `continuous=false` + `interimResults=true` —
 * одна реплика, но живой черновик по мере речи.
 */
export function startRecognition(options: StartRecognitionOptions): SpeechRecognitionLike {
  const recognition = new options.constructor();
  recognition.lang = options.locale === 'ru' ? 'ru-RU' : 'en-US';
  recognition.continuous = false;
  recognition.interimResults = true;

  // Полная гипотеза на каждый событие; замыкание хранит промежуточное
  // состояние, вызов onResult получает уже собранный текст.
  let hypothesis: TranscriptHypothesis = EMPTY_HYPOTHESIS;

  recognition.onstart = () => options.callbacks.onStart();
  recognition.onresult = (event) => {
    hypothesis = nextHypothesis(event, hypothesis);
    options.callbacks.onResult(hypothesisText(hypothesis));
  };
  recognition.onerror = () => options.callbacks.onError();
  recognition.onend = () => options.callbacks.onEnd();

  recognition.start();
  return recognition;
}
